import React, { useState, useEffect, useContext } from 'react'
import './FullSchedule.css'
import './FullScheduleCards.css'
import { Link, NavLink } from 'react-router-dom'
import { AuthContext } from '../context/auth-context'
import Footer from '../components/UIElements/Footer'

// ****************************************************************************************
//
// MOBILE VENUE SCHEDULE
//
// Every venue gets its OWN component instance and therefore its OWN
// showPreviousGames state.
//
// ****************************************************************************************

const VenueMobileSchedule = ({
	venue,
	allGamesAndEvents,
	formatGameDate,
	formatGameTime,
	getDisplayScore,
	getGameStatus,
	isGameOlderThanTwoDays,
	auth,
}) => {
	const [showPreviousGames, setShowPreviousGames] = useState(false)

	// Only games belonging to THIS venue
	const venueGames = allGamesAndEvents.filter(
		(game) => game.venueName === venue,
	)

	// By default, hide anything older than 2 days.
	// Clicking the link shows ALL games for THIS venue only.
	const gamesToDisplay = showPreviousGames
		? venueGames
		: venueGames.filter((game) => !isGameOlderThanTwoDays(game))

	return (
		<>
			<div
				onClick={() => setShowPreviousGames((prev) => !prev)}
				style={{
					textAlign: 'center',
					marginTop: '0.75rem',
					marginBottom: '1rem',
					fontSize: '0.85rem',
					fontWeight: '600',
					textDecoration: 'underline',
					cursor: 'pointer',
				}}
			>
				{showPreviousGames
					? 'Hide previous games'
					: 'Click to see previous games'}
			</div>

			<div className='scheduleCardsMobile'>
				{gamesToDisplay.map((game, key) => {
					return (
						<div
							className={`scheduleCard ${
								game.playoff || game.championship || game.description
									? 'scheduleCardSpecial'
									: ''
							}`}
							key={`mobile-schedule-${game._id || key}`}
						>
							<div className='scheduleCard__header'>
								<div>{formatGameDate(game)}</div>

								<div className='scheduleCard__league'>
									{game.leagueName || game.eventName || 'Game'}
								</div>

								<div>{formatGameTime(game)}</div>
							</div>

							{game.homeTeamName && game.visitorTeamName ? (
								<>
									<div className='scheduleCard__body'>
										<div className='scheduleCard__team'>
											<span
												className={
													game.winner === game.homeTeamId
														? 'scheduleCard__winnerPill'
														: ''
												}
											>
												{game.homeTeamSeed > 0 && `(${game.homeTeamSeed}) `}
												{game.homeTeamName}
											</span>
										</div>

										<div className='scheduleCard__scoreBox'>
											{game.score ? (
												<Link
													to={`/gameSummary/${game._id}`}
													className='scheduleCard__score'
												>
													{getDisplayScore(game)}
												</Link>
											) : (
												<div className='scheduleCard__score'>TBP</div>
											)}

											{game.score && (
												<div className='scheduleCard__status'>
													{getGameStatus(game)}
												</div>
											)}
										</div>

										<div className='scheduleCard__team'>
											<span
												className={
													game.winner === game.visitorTeamId
														? 'scheduleCard__winnerPill'
														: ''
												}
											>
												{game.visitorTeamSeed > 0 &&
													`(${game.visitorTeamSeed}) `}
												{game.visitorTeamName}
											</span>
										</div>
									</div>

									<div className='scheduleCard__venue'>📍 {game.venueName}</div>

									{game.description && (
										<div className='scheduleCard__description'>
											{game.description}
										</div>
									)}

									{game.championship && (
										<div className='scheduleCard__description'>
											CHAMPIONSHIP GAME
										</div>
									)}
								</>
							) : (
								<>
									<div className='scheduleCard__event'>
										{game.eventName || game.description || 'Scheduled Event'}
									</div>

									<div className='scheduleCard__venue'>📍 {game.venueName}</div>
								</>
							)}

							{auth.isLoggedIn && (
								<div className='scheduleCard__admin'>
									{game.playoff &&
										game.homeTeamName &&
										game.homeTeamName !== 'TBD' &&
										game.visitorTeamName !== 'TBD' && (
											<NavLink
												className='navlinks'
												to={`/admin/playoffGameResults2/${game._id}`}
											>
												Results
											</NavLink>
										)}

									{!game.playoff &&
										!game.championship &&
										game.homeTeamName &&
										game.homeTeamName !== 'TBD' &&
										game.visitorTeamName !== 'TBD' && (
											<NavLink
												className='navlinks'
												to={`/admin/gameResults2/${game._id}`}
												exact
											>
												Results
											</NavLink>
										)}

									{game.lastSubmittedByEmail && (
										<div className='fullScheduleSubmittedBy'>
											Submitted by:
											<br />
											{game.lastSubmittedByEmail}
										</div>
									)}

									{game.playoff &&
										!game.championship &&
										game.homeTeamName &&
										(game.homeTeamName === 'TBD' ||
											game.visitorTeamName === 'TBD') && (
											<NavLink
												className='navlinksEnterTeams'
												to={`/admin/updateGame/${game._id}`}
											>
												Teams
											</NavLink>
										)}
								</div>
							)}
						</div>
					)
				})}
			</div>
		</>
	)
}

// ****************************************************************************************
//
// FULL SCHEDULE
//
// ****************************************************************************************

const FullSchedule = () => {
	console.log('inside FullSchedule')

	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState()
	const [allGamesAndEvents, setAllGamesAndEvents] = useState([])

	const auth = useContext(AuthContext)

	// ****************************************************************************************
	//
	// GET ALL GAMES AND EVENTS
	//
	// ****************************************************************************************

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					process.env.REACT_APP_BACKEND_URL + '/admin/allGamesAndEvents',
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				setAllGamesAndEvents(responseData.allItems)

				setIsLoading(false)
			} catch (err) {
				setError(err.message)
			}

			setIsLoading(false)
		}

		sendRequest()
	}, [])

	// ****************************************************************************************
	//
	// FORMAT GAME DATE
	//
	// ****************************************************************************************

	const formatGameDate = (game) => {
		const months = {
			'01': 'Jan.',
			'02': 'Feb.',
			'03': 'Mar.',
			'04': 'Apr.',
			'05': 'May',
			'06': 'June',
			'07': 'July',
			'08': 'Aug.',
			'09': 'Sept.',
			10: 'Oct.',
			11: 'Nov.',
			12: 'Dec.',
		}

		return `${game.dayOfWeek}, ${
			months[game.date.substr(0, 2)]
		} ${game.date.substr(3, 2)}`
	}

	// ****************************************************************************************
	//
	// FORMAT GAME TIME
	//
	// ****************************************************************************************

	const formatGameTime = (game) => {
		const hour = game.time.substr(0, 2)
		const minute = game.time.substr(3, 2)

		const times = {
			'00': `12:${minute} am`,
			'01': `1:${minute} am`,
			'02': `2:${minute} am`,
			'03': `3:${minute} am`,
			'04': `4:${minute} am`,
			'05': `5:${minute} am`,
			'06': `6:${minute} am`,
			'07': `7:${minute} am`,
			'08': `8:${minute} am`,
			'09': `9:${minute} am`,
			10: `10:${minute} am`,
			11: `11:${minute} am`,
			12: `12:${minute} pm`,
			13: `1:${minute} pm`,
			14: `2:${minute} pm`,
			15: `3:${minute} pm`,
			16: `4:${minute} pm`,
			17: `5:${minute} pm`,
			18: `6:${minute} pm`,
			19: `7:${minute} pm`,
			20: `8:${minute} pm`,
			21: `9:${minute} pm`,
			22: `10:${minute} pm`,
			23: `11:${minute} pm`,
			24: `12:${minute} am`,
		}

		return times[hour]
	}

	// ****************************************************************************************
	//
	// GET DISPLAY SCORE
	//
	// ****************************************************************************************

	const getDisplayScore = (game) => {
		if (!game.score) {
			return 'TBP'
		}

		const scoreParts = game.score.split('-').map((score) => score.trim())

		if (scoreParts.length !== 2) {
			return game.score
		}

		const firstScore = Number(scoreParts[0])
		const secondScore = Number(scoreParts[1])

		if (Number.isNaN(firstScore) || Number.isNaN(secondScore)) {
			return game.score
		}

		if (game.winner === game.homeTeamId) {
			const homeScore = Math.max(firstScore, secondScore)

			const visitorScore = Math.min(firstScore, secondScore)

			return `${homeScore} - ${visitorScore}`
		}

		if (game.winner === game.visitorTeamId) {
			const homeScore = Math.min(firstScore, secondScore)

			const visitorScore = Math.max(firstScore, secondScore)

			return `${homeScore} - ${visitorScore}`
		}

		return `${firstScore} - ${secondScore}`
	}

	// ****************************************************************************************
	//
	// GET DISPLAY STATUS
	//
	// ****************************************************************************************

	const getGameStatus = (game) => {
		if (!game.score) {
			return ''
		}

		if (game.status === 'Shootout') {
			return 'SO'
		}

		if (game.status === 'Overtime') {
			return 'OT'
		}

		return 'FINAL'
	}

	// ****************************************************************************************
	//
	// ERROR HANDLER
	//
	// ****************************************************************************************

	const errorHandler = () => {
		setError(null)
	}

	// ****************************************************************************************
	//
	// IS GAME OLDER THAN TWO DAYS
	//
	// NSBHL DATE FORMAT IS:
	//
	// MM/DD/YYYY
	//
	// Example:
	//
	// 08/30/2026
	//
	// ****************************************************************************************

	const isGameOlderThanTwoDays = (game) => {
		if (!game.date) {
			return false
		}

		// Supports BOTH:
		// 08-30-2026
		// 08/30/2026
		const [month, day, year] = game.date.split(/[/-]/).map(Number)

		const gameDate = new Date(year, month - 1, day)
		gameDate.setHours(0, 0, 0, 0)

		const today = new Date()
		today.setHours(0, 0, 0, 0)

		const twoDaysAgo = new Date(today)
		twoDaysAgo.setDate(today.getDate() - 2)

		return gameDate < twoDaysAgo
	}

	// ****************************************************************************************
	//
	// GET DISTINCT VENUES
	//
	// ****************************************************************************************

	let distinctVenuesArray = []

	if (allGamesAndEvents.length !== 0) {
		distinctVenuesArray = [
			...new Set(allGamesAndEvents.map((game) => game.venueName)),
		]
	}

	// ****************************************************************************************
	//
	// NOTHING SCHEDULED
	//
	// ****************************************************************************************

	if (!isLoading && allGamesAndEvents.length === 0) {
		return (
			<React.Fragment>
				<div className='Leagues'>
					<h1>NOTHING SCHEDULED YET!</h1>
				</div>

				<div style={{ marginTop: '16rem' }}>
					<Footer />
				</div>
			</React.Fragment>
		)
	}

	// ****************************************************************************************
	//
	// BUILD SCHEDULE BY VENUE
	//
	// ****************************************************************************************

	let dateBlock = []

	if (!isLoading && allGamesAndEvents.length !== 0 && distinctVenuesArray) {
		distinctVenuesArray.forEach((venue) => {
			dateBlock.push(
				<div className='Schedule' key={venue}>
					<h1>{venue}</h1>

					{/* **************************************************************
					    DESKTOP TABLE
					************************************************************** */}

					<table id='defaultScheduleDisplay'>
						<thead>
							<tr>
								<th>Date/Time</th>

								<th>Game or Event</th>

								<th></th>

								<th></th>

								{auth.isLoggedIn && <th></th>}

								<th>Winner</th>

								<th>Score</th>
							</tr>
						</thead>

						<tbody>
							{allGamesAndEvents.map((game, key) => {
								if (game.venueName !== venue) {
									return null
								}

								return (
									<tr key={game._id || key}>
										<td>
											{formatGameDate(game)}

											<br />

											{formatGameTime(game)}
										</td>

										<td>{game.leagueName || game.eventName}</td>

										{game.visitorTeamName && (
											<td>
												{game.visitorTeamSeed > 0 &&
													'(' + game.visitorTeamSeed + ') '}

												{game.visitorTeamName}

												<br />

												{' @ '}

												<br />

												{game.homeTeamSeed > 0 &&
													'(' + game.homeTeamSeed + ') '}

												{game.homeTeamName}

												{game.description && (
													<div
														style={{
															color: 'red',
															fontWeight: 'bold',
														}}
													>
														({game.description})
													</div>
												)}

												{game.championship && (
													<div
														style={{
															color: 'red',
															fontWeight: 'bold',
														}}
													>
														CHAMPIONSHIP GAME
													</div>
												)}
											</td>
										)}

										{game.playoff && <td></td>}

										{game.championship && <td></td>}

										{game.eventName && (
											<td
												style={{
													fontWeight: 'bold',
													backgroundColor: 'pink',
												}}
											>
												{game.eventName}
											</td>
										)}

										{!game.playoff && !game.championship && <td></td>}

										{auth.isLoggedIn && (
											<td>
												{game.playoff &&
													game.homeTeamName &&
													game.homeTeamName !== 'TBD' &&
													game.visitorTeamName !== 'TBD' && (
														<NavLink
															className='navlinks'
															to={`/admin/playoffGameResults2/${game._id}`}
															exact
														>
															Results
														</NavLink>
													)}

												{!game.playoff &&
													!game.championship &&
													game.homeTeamName &&
													game.homeTeamName !== 'TBD' &&
													game.visitorTeamName !== 'TBD' && (
														<NavLink
															className='navlinks'
															to={`/admin/gameResults2/${game._id}`}
															exact
														>
															Results
														</NavLink>
													)}

												{game.lastSubmittedByEmail && (
													<div className='fullScheduleSubmittedBy'>
														Submitted by:
														<br />
														{game.lastSubmittedByEmail}
													</div>
												)}

												{game.playoff &&
													!game.championship &&
													game.homeTeamName &&
													(game.homeTeamName === 'TBD' ||
														game.visitorTeamName === 'TBD') && (
														<NavLink
															className='navlinksEnterTeams'
															to={`/admin/updateGame/${game._id}`}
															exact
														>
															Teams
														</NavLink>
													)}
											</td>
										)}

										{game.homeTeamId && game.winner === game.homeTeamId && (
											<td>{game.homeTeamName}</td>
										)}

										{game.visitorTeamId &&
											game.winner === game.visitorTeamId && (
												<td>{game.visitorTeamName}</td>
											)}

										{game.status &&
											game.status !== 'TBP' &&
											game.winner !== game.homeTeamId &&
											game.winner !== game.visitorTeamId && <td>TIE</td>}

										{!game.score && <td></td>}

										<td>
											{game.score && (
												<Link
													style={{
														textDecoration: 'none',
														fontWeight: 600,
														color: 'black',
													}}
													to={`/gameSummary/${game._id}`}
												>
													{game.score}

													{game.status === 'Overtime' && ' (OT)'}

													{game.status === 'Shootout' && ' (SO)'}
												</Link>
											)}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>

					{/* **************************************************************
					    MOBILE CARDS

					    IMPORTANT:
					    This is a completely separate component for THIS venue.

					    Therefore every venue independently starts:
					    showPreviousGames = false
					************************************************************** */}

					<VenueMobileSchedule
						venue={venue}
						allGamesAndEvents={allGamesAndEvents}
						formatGameDate={formatGameDate}
						formatGameTime={formatGameTime}
						getDisplayScore={getDisplayScore}
						getGameStatus={getGameStatus}
						isGameOlderThanTwoDays={isGameOlderThanTwoDays}
						auth={auth}
					/>
				</div>,
			)
		})
	}

	// ****************************************************************************************
	//
	// RETURN
	//
	// ****************************************************************************************

	return (
		<div style={{ overflowX: 'hidden' }}>
			<React.Fragment>
				{dateBlock}

				<Footer />
			</React.Fragment>
		</div>
	)
}

export default FullSchedule
